import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EmptyMessage from "../../Component/EmptyMessage";
import MyInterestCard from "../../Component/MyInterestCard";
import "./EditInterest.css";
import { useDispatch, useSelector } from "react-redux";
import { skeletonAction } from "../../Redux/Action/loadingAction";
import InfinitScroll from "react-infinite-scroll-component";
import {
  getEditInterests,
  updateCheckboxInterest,
} from "../../Redux/Action/BusinessAction/businesAction";

const EditInterest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [responseData, setResponseData] = useState([]);

  const [pageCount, setCount] = useState(1);
  const [initialSelectionsLoaded, setInitialSelectionsLoaded] = useState(false);
  const [allLoadedInterests, setAllLoadedInterests] = useState([]);

  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const PageSize = 20;

  const skeletonLoad = useSelector(
    (state) => state?.loadingReducers?.skeletonLoader
  );
  const user_uuid = localStorage.getItem("user_uuid");

  // Function to sort interests with selected ones first
  const sortInterestsBySelection = (interests, selectedIds) => {
    return [...interests].sort((a, b) => {
      if (a.status === 1 && b.status !== 1) return -1;
      if (a.status !== 1 && b.status === 1) return 1;
      return 0;
    });
  };

  const fetchData = useCallback(
    async (search = "", page = pageCount) => {
      const payload = {
        type: "Custom",
        user_uuid: user_uuid,
        page,
        limit: PageSize,
        text: search,
      };

      dispatch(skeletonAction(true, "EDIT_INTEREST"));
      try {
        const response = await dispatch(getEditInterests(payload));
        if (response?.data?.status === 200) {
          let newData;
          let newItems = []; // Define newItems at the top level

          if (page === 1) {
            newData = sortInterestsBySelection(
              [...response.data.result.interestList],
              []
            );

            // Set selected interests based on status
            if (!initialSelectionsLoaded && search === "") {
              const newSelectedInterests = newData
                .filter((item) => item.status === 1)
                .map((item) => item.id);
              setSelectedInterests(newSelectedInterests);
              setInitialSelectionsLoaded(true);
            }

            // Update allLoadedInterests when search changes
            if (search === "") {
              setAllLoadedInterests(newData);
            } else {
              // When searching, merge new interests with already loaded ones
              const newIds = new Set(newData.map((item) => item.id));
              const mergedInterests = [
                ...newData,
                ...allLoadedInterests.filter((item) => !newIds.has(item.id)),
              ];
              setAllLoadedInterests(mergedInterests);
            }
            setResponseData(newData);
          } else {
            // For subsequent pages, append new data
            newItems = response.data.result.interestList.filter(
              (newItem) =>
                !responseData.some((existing) => existing.id === newItem.id)
            );
            setResponseData((prev) => [...prev, ...newItems]);

            const newIds = new Set(newItems.map((item) => item.id));
            const mergedInterests = [
              ...allLoadedInterests,
              ...newItems.filter(
                (item) =>
                  !allLoadedInterests.some(
                    (existing) => existing.id === item.id
                  )
              ),
            ];
            setAllLoadedInterests(mergedInterests);
          }

          const totalRecords = response.data.result.TotalRecords;
          // Use the appropriate data length based on whether it's the first page or not
          const currentDataLength =
            page === 1 ? newData.length : responseData.length + newItems.length;
          setHasMore(currentDataLength < totalRecords);
          setIsFetchingMore(false);
        }
      } catch (error) {
        console.error("Error fetching interests:", error);
        setIsFetchingMore(false);
      } finally {
        dispatch(skeletonAction(false, "EDIT_INTEREST"));
      }
    },
    [
      pageCount,
      user_uuid,
      dispatch,
      initialSelectionsLoaded,
      responseData,
      allLoadedInterests,
    ]
  );

  const fetchMoreData = () => {
    if (!skeletonLoad["EDIT_INTEREST"] && hasMore && !isFetchingMore) {
      setIsFetchingMore(true);
      setCount((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (pageCount > 1) {
      fetchData("", pageCount);
    }
  }, [pageCount]);

  useEffect(() => {
    fetchData("", 1);
  }, []);

  useEffect(() => {
    // Update selectAll based on responseData
    if (responseData.length > 0) {
      const allSelected = responseData.every((i) =>
        selectedInterests.includes(i.id)
      );
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [responseData, selectedInterests]);

  const handleSelectAll = () => {
    const visibleIds = responseData.map((i) => i.id);
    const allVisibleSelected = visibleIds.every((id) =>
      selectedInterests.includes(id)
    );
    setSelectedInterests((prev) => {
      return allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : [...new Set([...prev, ...visibleIds])];
    });
  };

  const handleSaveChanges = async () => {
    dispatch(skeletonAction(true, "EDIT_INTEREST"));
    try {
      // Get all interest IDs from all loaded interests
      const allIds = allLoadedInterests.map((i) => i.id);

      // Filter to identify which interests should be marked as "not interested"
      const toUnselect = allIds.filter((id) => !selectedInterests.includes(id));

      const payload = { id: toUnselect };
      const response = await dispatch(updateCheckboxInterest(payload));

      if (response?.payload?.status === 200) {
        navigate("/my-interest", {
          state: { fromEdit: true, timestamp: Date.now() },
        });
      }
    } catch (error) {
      console.error("Error updating interests:", error);
    } finally {
      dispatch(skeletonAction(false, "EDIT_INTEREST"));
    }
  };

  const handleInterestSelect = (interestId) => {
    setSelectedInterests((prev) =>
      prev.includes(interestId)
        ? prev.filter((id) => id !== interestId)
        : [...prev, interestId]
    );
  };

  return (
    <div className="edit-interest-container">
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <img
              src={"../images/back_btn.png"}
              alt="Back"
              height={25}
              width={76}
              onClick={() => navigate("/my-interest")}
              className="back-button"
            />
          </div>
        </div>
      </div>

      <section className="nib-boxes">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="search-section">
                <div className="search-container">
                  <form onSubmit={(e) => e.preventDefault()}>
                    <input
                      type="text"
                      name="search"
                      placeholder="Search"
                      onChange={(e) => setSearch(e.target.value)}
                      value={search}
                      className="search-input"
                      style={{ width: "502px" }}
                    />
                  </form>
                  <button
                    className="search-btn"
                    type="button"
                    onClick={() => {
                      setCount(1);
                      fetchData(search, 1);
                    }}
                  >
                    Search
                  </button>
                </div>

                <div className="controls-container">
                  <label className="select-all-label">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="select-all-checkbox"
                    />
                    {selectAll ? "Unselect All" : "Select All"}
                  </label>

                  <button
                    className="save-changes-btn"
                    onClick={handleSaveChanges}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>

            {!skeletonLoad["EDIT_INTEREST"] && responseData.length === 0 ? (
              <EmptyMessage
                message={`No interests found ${
                  search ? `for "${search}"` : ""
                }`}
              />
            ) : (
              <div
                id="scrollableDiv"
                style={{
                  height: "calc(100vh - 280px)",
                  overflow: "auto",
                  padding: "0 15px",
                }}
              >
                <InfinitScroll
                  dataLength={responseData.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  scrollableTarget="scrollableDiv"
                  style={{ overflow: "auto", overflowX: "hidden" }}
                  loader={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "20px",
                        marginTop: "10px",
                      }}
                    >
                      {isFetchingMore && (
                        <div className="loading-spinner">
                          <div className="spinner"></div>
                          <p>Loading...</p>
                        </div>
                      )}
                    </div>
                  }
                >
                  <div className="interests-grid">
                    {skeletonLoad["EDIT_INTEREST"] && pageCount === 1
                      ? Array(5)
                          .fill(0)
                          .map((_, index) => (
                            <div
                              key={index}
                              style={{ width: "100%", height: "287px" }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  backgroundColor: "#333",
                                  borderRadius: "8px",
                                }}
                              />
                            </div>
                          ))
                      : responseData.map((interest) => (
                          <div key={interest.id}>
                            <MyInterestCard
                              data={{
                                id: interest.id,
                                title: interest.category_name,
                                image: interest.category_image,
                              }}
                              // width="100%"
                              width={132}
                              height={132}
                              isEditMode={true}
                              isSelected={selectedInterests.includes(
                                interest.id
                              )}
                              onSelect={handleInterestSelect}
                            />
                          </div>
                        ))}
                  </div>
                </InfinitScroll>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default EditInterest;
